import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import SEOHead from '../components/SEOHead';
import { 
  trackCalculatorCompleted, 
  trackCalculatorStarted,
  trackTextButtonClicked, 
  trackCalendarButtonClicked, 
  trackShareButtonClicked,
  setFunnelPosition
} from '../utils/analytics';
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  Calculator as CalcIcon,
  Share2,
  MessageSquare,
  Mail,
  Copy,
  Phone,
  Calendar,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const calculatorQuestions = [
  {
    id: 'currentWeight',
    question: 'What is your current weight?',
    subtitle: 'This helps us calculate your personalized forecast',
    type: 'number',
    unit: 'lbs',
    placeholder: 'e.g. 220'
  },
  {
    id: 'goalWeight',
    question: 'What is your goal weight?',
    subtitle: 'Where would you like to be?',
    type: 'number',
    unit: 'lbs',
    placeholder: 'e.g. 180'
  },
  {
    id: 'height',
    question: 'What is your height?',
    subtitle: 'Used to personalize your estimate',
    type: 'height'
  },
  {
    id: 'age',
    question: 'How old are you?',
    subtitle: 'Age affects metabolic calculations',
    type: 'number',
    placeholder: 'e.g. 42'
  },
  {
    id: 'activityLevel',
    question: 'How would you describe your current activity level?',
    subtitle: 'Activity affects your results',
    type: 'select',
    options: [
      { value: 'sedentary', label: 'Mostly sedentary', desc: 'Desk job, minimal exercise' },
      { value: 'lightly-active', label: 'Lightly active', desc: 'Light walks, some movement' },
      { value: 'moderately-active', label: 'Moderately active', desc: 'Exercise 3x per week' },
      { value: 'very-active', label: 'Very active', desc: 'Exercise 5+ days per week' }
    ]
  },
  {
    id: 'medication',
    question: 'Which medication are you most interested in or currently using?',
    subtitle: 'Different medications have different average results',
    type: 'select',
    options: [
      { value: 'semaglutide', label: 'Semaglutide', desc: 'Ozempic/Wegovy' },
      { value: 'tirzepatide', label: 'Tirzepatide', desc: 'Mounjaro/Zepbound' },
      { value: 'not-sure', label: 'Not sure yet', desc: 'Help me understand the options' },
      { value: 'already-on', label: 'Already on one', desc: 'I want to see my forecast' }
    ]
  },
  {
    id: 'previousAttempts',
    question: 'Have you tried weight loss programs or medications before?',
    subtitle: 'Your history helps us understand your journey',
    type: 'select',
    options: [
      { value: 'first-time', label: 'This would be my first time', desc: 'New to medical weight loss' },
      { value: 'diet-exercise', label: 'Yes — diet and exercise programs', desc: 'Traditional approaches' },
      { value: 'previous-meds', label: 'Yes — previous medications', desc: 'Tried GLP-1s or other meds' },
      { value: 'multiple', label: 'Yes — multiple programs and/or medications', desc: 'Extensive experience' }
    ]
  },
  {
    id: 'biggestChallenge',
    question: "What's your biggest challenge right now?",
    subtitle: 'This helps us personalize your insights',
    type: 'select',
    options: [
      { value: 'appetite', label: 'Controlling appetite and cravings', desc: 'Food is always on my mind' },
      { value: 'consistency', label: 'Staying consistent with a program', desc: 'Hard to stick with it' },
      { value: 'affordability', label: 'Finding something I can afford', desc: 'Cost is a major factor' },
      { value: 'choosing', label: 'Understanding which option is right for me', desc: 'Need guidance on options' }
    ]
  }
];

export const Calculator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    heightFeet: '',
    heightInches: ''
  });
  const [showResults, setShowResults] = useState(false);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);

  // Track calculator started on first render
  useEffect(() => {
    if (!hasTrackedStart) {
      // Determine source from referrer or state
      const source = location.state?.source || 
                     (document.referrer.includes('/blog') ? 'blog' : 
                      document.referrer.includes('/start-here') ? 'start_here' : 'direct');
      trackCalculatorStarted(source);
      setFunnelPosition(2);
      setHasTrackedStart(true);
    }
  }, [hasTrackedStart, location.state, setFunnelPosition, trackCalculatorStarted]);

  const totalSteps = calculatorQuestions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentQuestion = calculatorQuestions[currentStep];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleHeightChange = (field, value) => {
    setAnswers({ ...answers, [field]: value });
  };

  const canProceed = () => {
    const q = currentQuestion;
    if (q.type === 'height') {
      return answers.heightFeet && answers.heightInches !== '';
    }
    return answers[q.id] !== undefined && answers[q.id] !== '';
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Forecast calculation logic
  const calculateForecast = () => {
    const currentWeight = parseFloat(answers.currentWeight) || 220;
    const goalWeight = parseFloat(answers.goalWeight) || 180;
    const medication = answers.medication || 'tirzepatide';
    const activity = answers.activityLevel || 'lightly-active';

    // Base monthly loss rate by medication (% of body weight)
    let lowRate, highRate;
    if (medication === 'semaglutide') {
      lowRate = 0.012; // 1.2%
      highRate = 0.018; // 1.8%
    } else {
      // tirzepatide, not-sure, already-on use tirzepatide rates
      lowRate = 0.015; // 1.5%
      highRate = 0.022; // 2.2%
    }

    // Activity multiplier
    const activityMultipliers = {
      'sedentary': 0.85,
      'lightly-active': 1.0,
      'moderately-active': 1.1,
      'very-active': 1.15
    };
    const multiplier = activityMultipliers[activity] || 1.0;

    // Calculate 90-day (3 month) estimates
    const lowEstimate = Math.round(currentWeight * lowRate * multiplier * 3);
    const highEstimate = Math.round(currentWeight * highRate * multiplier * 3);

    // Cap at 85% of total weight to lose
    const totalToLose = currentWeight - goalWeight;
    const maxLoss = Math.round(totalToLose * 0.85);

    const cappedLow = Math.min(lowEstimate, maxLoss);
    const cappedHigh = Math.min(highEstimate, maxLoss);

    return {
      currentWeight,
      goalWeight,
      lowEstimate: cappedLow,
      highEstimate: cappedHigh,
      projectedLow: currentWeight - cappedLow,
      projectedHigh: currentWeight - cappedHigh
    };
  };

  const getMedicationInsight = () => {
    const medication = answers.medication;
    if (medication === 'semaglutide') {
      return "Semaglutide works by activating GLP-1 receptors to reduce appetite and stabilize blood sugar — most people notice significant appetite reduction within the first 2 weeks.";
    } else if (medication === 'tirzepatide') {
      return "Tirzepatide activates two receptors (GLP-1 and GIP) and shows the strongest average results in current clinical data — your estimate reflects this.";
    } else {
      return "Based on your profile, both options could be effective — the main differences are mechanism, average results, and cost. We can walk you through which makes more sense for you.";
    }
  };

  const getChallengeInsight = () => {
    const challenge = answers.biggestChallenge;
    const insights = {
      'appetite': "Your biggest opportunity: GLP-1 medications directly target appetite regulation in the brain — most people report a significant reduction in food cravings within the first 2 weeks. This is where the medication makes the biggest immediate difference for people like you.",
      'consistency': "Your biggest opportunity: GLP-1 programs work differently from willpower-based approaches — the medication handles the appetite regulation, which removes the primary reason most programs fail for people who struggle with consistency.",
      'affordability': "Your biggest opportunity: Compounded GLP-1 options through a medically supervised program are significantly less expensive than brand-name medications — often $200–$400/month compared to $900–$1,300 for brand-name options.",
      'choosing': "Your biggest opportunity: The choice between semaglutide and tirzepatide comes down to a few specific factors. We can walk you through which is a better fit for your profile in a quick conversation."
    };
    return insights[challenge] || insights['appetite'];
  };

  const getBlogRecommendations = () => {
    const challenge = answers.biggestChallenge;
    const recommendations = {
      'appetite': [
        { title: 'How GLP-1 Medications Actually Work', url: '/blog/how-glp1-medications-work' },
        { title: 'What to Expect in Your First 30 Days', url: '/blog/first-30-days-glp1-program' }
      ],
      'affordability': [
        { title: 'Compounded vs Brand Name GLP-1', url: '/blog/compounded-vs-brand-name-glp1' },
        { title: 'What Is Compounded Semaglutide?', url: '/blog/what-is-compounded-semaglutide' }
      ],
      'consistency': [
        { title: 'Common GLP-1 Side Effects and How to Fix Them', url: '/blog/glp1-side-effects-how-to-fix' },
        { title: 'Sleep, Stress, and Weight Loss', url: '/blog/sleep-stress-glp1-weight-loss' }
      ],
      'choosing': [
        { title: 'What Is Tirzepatide?', url: '/blog/what-is-tirzepatide' },
        { title: 'How GLP-1 Medications Actually Work', url: '/blog/how-glp1-medications-work' }
      ]
    };
    return recommendations[challenge] || recommendations['appetite'];
  };

  const handleShare = (method) => {
    const shareUrl = `${window.location.origin}/calculator`;
    const shareMessage = `Hey — I just used this tool that builds a personalized 90-day weight loss estimate based on your numbers. It's not a quiz, it actually gives you a realistic forecast. Takes 3 minutes and you can see your results before they ask for anything. ${window.location.host}/calculator`;

    // Track share button click
    trackShareButtonClicked(method);

    if (method === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } else if (method === 'sms') {
      window.location.href = `sms:?body=${encodeURIComponent(shareMessage)}`;
    } else if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
    } else if (method === 'email') {
      window.location.href = `mailto:?subject=Check out this 90-day weight loss forecast tool&body=${encodeURIComponent(shareMessage)}`;
    }
  };

  // Track calculator completion when results are shown
  useEffect(() => {
    if (showResults) {
      trackCalculatorCompleted(answers);
      setFunnelPosition(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults]);

  if (showResults) {
    const forecast = calculateForecast();
    const blogRecs = getBlogRecommendations();

    return (
      <div className="min-h-screen bg-[#0B1020] px-4 py-12">
        <SEOHead 
          title="Your 90-Day Weight Loss Forecast Results"
          description="Your personalized 90-day weight loss forecast based on your profile. See your estimated results with GLP-1 medications."
          canonicalPath="/calculator"
        />
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[#94A3B8] hover:text-[#F8FAFC] mb-4"
            >
              ← Back to Home
            </Button>
          </div>

          {/* Results Card */}
          <Card className="bg-[#121A2B] border-[#1E293B] p-8 sm:p-12 mb-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#22C55E] flex items-center justify-center mx-auto mb-4">
                <CalcIcon className="text-white" size={32} />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-2">
                Your 90-Day Forecast
              </h1>
              <p className="text-[#94A3B8]">
                Based on your profile, here's what a medically guided program could look like
              </p>
            </div>

            {/* Forecast Display */}
            <div className="bg-[#0B1020] border border-[#1E293B] rounded-xl p-6 mb-8">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-[#94A3B8] text-sm mb-1">Current Weight</p>
                  <p className="text-3xl font-bold text-[#F8FAFC]">{forecast.currentWeight} <span className="text-lg text-[#94A3B8]">lbs</span></p>
                </div>
                <div>
                  <p className="text-[#94A3B8] text-sm mb-1">Estimated Loss</p>
                  <p className="text-3xl font-bold text-[#22C55E]">{forecast.lowEstimate}–{forecast.highEstimate} <span className="text-lg text-[#94A3B8]">lbs</span></p>
                </div>
                <div>
                  <p className="text-[#94A3B8] text-sm mb-1">Projected at 90 Days</p>
                  <p className="text-3xl font-bold text-[#3B82F6]">{forecast.projectedHigh}–{forecast.projectedLow} <span className="text-lg text-[#94A3B8]">lbs</span></p>
                </div>
              </div>
            </div>

            {/* Medication Insight */}
            <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg p-4 mb-6">
              <p className="text-[#F8FAFC] text-sm">{getMedicationInsight()}</p>
            </div>

            {/* Challenge Insight */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-3">Your Personalized Insight</h3>
              <p className="text-[#94A3B8]">{getChallengeInsight()}</p>
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-[#64748B] text-center mb-8">
              These estimates are based on clinical data averages and are not a guarantee of individual results. Actual outcomes vary based on adherence, dosing, individual physiology, and medical history. This tool is for informational purposes only and does not constitute medical advice.
            </p>

            {/* Conversion CTAs */}
            <div className="space-y-4 mb-8">
              <p className="text-center text-[#94A3B8] text-sm">
                Based on your forecast, we can map out your exact plan and get you started this week. Want me to walk you through it?
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => {
                    trackTextButtonClicked();
                    window.location.href = `sms:${process.env.REACT_APP_CONTACT_PHONE || '8016518363'}`;
                  }}
                  className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg py-6 h-auto"
                  data-testid="text-results-btn"
                >
                  <MessageSquare className="mr-2" size={20} />
                  Text Me My Results
                </Button>
                <Button
                  onClick={() => {
                    trackCalendarButtonClicked();
                    window.open(process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/bryanm8623/30min', '_blank');
                  }}
                  className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg py-6 h-auto"
                  data-testid="schedule-chat-btn"
                >
                  <Calendar className="mr-2" size={20} />
                  Let's Map Out Your Plan
                </Button>
              </div>
              <p className="text-center text-[#64748B] text-xs">
                No commitment required. Most people just want to ask a few questions first — that's completely fine.
              </p>
            </div>

            {/* Not Ready Yet Section */}
            <div className="border-t border-[#1E293B] pt-8 mb-8">
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-2 flex items-center gap-2">
                <BookOpen size={20} className="text-[#3B82F6]" />
                Not ready yet?
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Keep learning at your own pace. Based on your answers, these articles might help:
              </p>
              <div className="space-y-3">
                {blogRecs.map((rec, idx) => (
                  <a
                    key={idx}
                    href={rec.url}
                    className="block p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg hover:border-[#3B82F6] transition-all duration-300"
                    data-testid={`blog-rec-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[#F8FAFC] font-medium">{rec.title}</span>
                      <ArrowRight size={16} className="text-[#3B82F6]" />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Share Section */}
            <div className="border-t border-[#1E293B] pt-8">
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-2 flex items-center gap-2">
                <Share2 size={20} className="text-[#22C55E]" />
                Know someone who's been curious about this?
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Send them this tool — they can build their own forecast in 3 minutes. No forms, no sales calls required to see their results.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleShare('copy')}
                  variant="outline"
                  className="border-[#1E293B] bg-[#0B1020] hover:bg-[#1E293B] text-[#F8FAFC]"
                  data-testid="share-copy-btn"
                >
                  <Copy size={16} className="mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={() => handleShare('sms')}
                  variant="outline"
                  className="border-[#1E293B] bg-[#0B1020] hover:bg-[#1E293B] text-[#F8FAFC]"
                  data-testid="share-sms-btn"
                >
                  <MessageSquare size={16} className="mr-2" />
                  SMS
                </Button>
                <Button
                  onClick={() => handleShare('whatsapp')}
                  variant="outline"
                  className="border-[#1E293B] bg-[#0B1020] hover:bg-[#1E293B] text-[#F8FAFC]"
                  data-testid="share-whatsapp-btn"
                >
                  WhatsApp
                </Button>
                <Button
                  onClick={() => handleShare('email')}
                  variant="outline"
                  className="border-[#1E293B] bg-[#0B1020] hover:bg-[#1E293B] text-[#F8FAFC]"
                  data-testid="share-email-btn"
                >
                  <Mail size={16} className="mr-2" />
                  Email
                </Button>
              </div>
            </div>
          </Card>

          {/* Footer */}
          <div className="text-center">
            <p className="text-[#64748B] text-sm">
              All the information on this site is educational. Nothing here constitutes medical advice. Bryan Maldonado is a licensed professional and all programs include medical oversight.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Question Flow
  return (
    <div className="min-h-screen bg-[#0B1020] px-4 py-12">
      <SEOHead 
        title="90-Day Weight Loss Forecast Calculator"
        description="Build your personalized 90-day weight loss forecast based on your profile. Get a realistic estimate of what GLP-1 medications could mean for you — no consultation required."
        canonicalPath="/calculator"
      />
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex justify-between items-center mb-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F8FAFC]"
          >
            ← Back to Home
          </Button>
          <span className="text-[#94A3B8] text-sm">
            Question {currentStep + 1} of {totalSteps}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full h-2 bg-[#1E293B] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#3B82F6] to-[#22C55E] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Page Title (shown on first question) */}
      {currentStep === 0 && (
        <div className="max-w-4xl mx-auto text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-2">
            Your 90-Day Weight Loss Forecast
          </h1>
          <p className="text-[#94A3B8]">
            This isn't a quiz. It's a personalized estimate — built on your numbers, not someone else's average.
          </p>
        </div>
      )}

      {/* Question Card */}
      <div className="max-w-4xl mx-auto">
        <Card className="bg-[#121A2B] border-[#1E293B] p-8 sm:p-12">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] mb-3">
              {currentQuestion.question}
            </h2>
            <p className="text-[#94A3B8]">{currentQuestion.subtitle}</p>
          </div>

          {/* Number Input */}
          {currentQuestion.type === 'number' && (
            <div className="max-w-xs">
              <div className="relative">
                <Input
                  type="number"
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] text-2xl py-6 pr-16 focus:border-[#3B82F6]"
                  data-testid={`input-${currentQuestion.id}`}
                />
                {currentQuestion.unit && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] text-lg">
                    {currentQuestion.unit}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Height Input */}
          {currentQuestion.type === 'height' && (
            <div className="flex gap-4 max-w-xs">
              <div className="flex-1">
                <Label className="text-[#94A3B8] text-sm mb-2 block">Feet</Label>
                <Input
                  type="number"
                  value={answers.heightFeet || ''}
                  onChange={(e) => handleHeightChange('heightFeet', e.target.value)}
                  placeholder="5"
                  min="3"
                  max="8"
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] text-2xl py-6 focus:border-[#3B82F6]"
                  data-testid="input-height-feet"
                />
              </div>
              <div className="flex-1">
                <Label className="text-[#94A3B8] text-sm mb-2 block">Inches</Label>
                <Input
                  type="number"
                  value={answers.heightInches || ''}
                  onChange={(e) => handleHeightChange('heightInches', e.target.value)}
                  placeholder="10"
                  min="0"
                  max="11"
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] text-2xl py-6 focus:border-[#3B82F6]"
                  data-testid="input-height-inches"
                />
              </div>
            </div>
          )}

          {/* Select Options */}
          {currentQuestion.type === 'select' && (
            <RadioGroup 
              value={answers[currentQuestion.id]} 
              onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
            >
              <div className="space-y-4">
                {currentQuestion.options.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-4 p-6 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                      answers[currentQuestion.id] === option.value
                        ? 'border-[#3B82F6] bg-[#3B82F6]/10'
                        : 'border-[#1E293B] hover:border-[#3B82F6]/50 bg-[#0B1020]'
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} className="mt-1" />
                    <div className="flex-1">
                      <div className="font-bold text-[#F8FAFC] text-lg mb-1">{option.label}</div>
                      <div className="text-[#94A3B8] text-sm">{option.desc}</div>
                    </div>
                    {answers[currentQuestion.id] === option.value && (
                      <CheckCircle2 className="text-[#3B82F6] flex-shrink-0" size={24} />
                    )}
                  </label>
                ))}
              </div>
            </RadioGroup>
          )}

          {/* Navigation */}
          <div className="flex gap-4 mt-8">
            {currentStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-[#1E293B] bg-transparent hover:bg-[#1E293B] text-[#F8FAFC]"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex-1 text-lg py-6 h-auto ${
                canProceed()
                  ? 'bg-[#3B82F6] hover:bg-[#2563EB] text-white'
                  : 'bg-[#1E293B] text-[#94A3B8] cursor-not-allowed'
              }`}
              data-testid="next-btn"
            >
              {currentStep === totalSteps - 1 ? 'See My Forecast' : 'Next'}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Calculator;
